import type { CrossSectionDataSet, YToolDataContext } from "./d";
import {
  generateEquipmentDataPoints,
  generateCrossSectionDataSetTitle
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import type { ConvertableUnit } from "@/libs/units";

export interface YToolBypassPositioningParams {
  casingDriftRadius: number;
  yToolWidthIn: number;
  bypassTubingOuterDiameterIn: number;
  lengthUnit: ConvertableUnit;
}

export interface YToolBypassPositioningResult {
  bypassTubingDimRadius: number;
  bypassTubingDimCenterX: number;
  bypassTubingDimCenterY: number;
  bypassTubingDimPoints: Array<{ x: number; y: number }>;
  bypassTubingDataSet: CrossSectionDataSet;
  minEffectiveYToolDimRadius: number;
  minEffectiveYToolDimCenterX: number;
}

/**
 * Calculates Y-Tool bypass tubing positioning and generates the dataset
 * This logic is shared across multiple chart generators to ensure consistency
 *
 * @param params Parameters for calculating bypass positioning
 * @returns Positioning data and dataset for the bypass tubing
 */
export function getYToolBypassPositioning(
  params: YToolBypassPositioningParams
): YToolBypassPositioningResult {
  const {
    casingDriftRadius,
    yToolWidthIn,
    bypassTubingOuterDiameterIn,
    lengthUnit
  } = params;

  // Min Effective Y-Tool Dim
  const minEffectiveYToolDimRadius = yToolWidthIn / 2;
  const minEffectiveYToolDimCenterX =
    minEffectiveYToolDimRadius - casingDriftRadius;

  // Y-Tool Bypass Tubing Dim circle
  const bypassTubingDimRadius = bypassTubingOuterDiameterIn / 2;
  const bypassTubingDimCenterX =
    minEffectiveYToolDimCenterX -
    minEffectiveYToolDimRadius +
    bypassTubingDimRadius;
  const bypassTubingDimCenterY = 0;

  const bypassTubingDimPoints = generateEquipmentDataPoints(
    bypassTubingDimRadius,
    bypassTubingDimCenterX,
    bypassTubingDimCenterY
  );

  const bypassTubingDataSet: CrossSectionDataSet = {
    title: generateCrossSectionDataSetTitle(
      "Y Block Bypass Tubing OD",
      bypassTubingOuterDiameterIn,
      lengthUnit
    ),
    xAxisLabel: `${lengthUnit.label}`,
    yAxisLabel: `${lengthUnit.label}`,
    points: bypassTubingDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, lengthUnit),
        y: convertSmallLengthUnits(pt.y, lengthUnit)
      };
    })
  };

  return {
    bypassTubingDimRadius,
    bypassTubingDimCenterX,
    bypassTubingDimCenterY,
    bypassTubingDimPoints,
    bypassTubingDataSet,
    minEffectiveYToolDimRadius,
    minEffectiveYToolDimCenterX
  };
}

/**
 * Generates datasets representing the y tool cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the y tools
 */
export const getYToolChartDataSet = (
  yToolDataContext: YToolDataContext
): CrossSectionDataSet[] => {
  const yToolChartDataSet: CrossSectionDataSet[] = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = yToolDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = yToolDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );
  yToolChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing ID",
      yToolDataContext.casing.innerDiameterIn,
      yToolDataContext.lengthUnit
    ),
    xAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    yAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    points: casingInnerPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, yToolDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, yToolDataContext.lengthUnit)
      };
    })
  });
  yToolChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing Drift ID",
      yToolDataContext.casing.driftDiameterIn,
      yToolDataContext.lengthUnit
    ),
    xAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    yAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    points: casingDriftPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, yToolDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, yToolDataContext.lengthUnit)
      };
    })
  });

  // Min Effective Y-Tool Dim radius and center X
  // (needed for bypass tubing positioning)
  const minEffectiveYToolDimRadius = yToolDataContext.widthIn / 2;
  const minEffectiveYToolDimCenterX =
    minEffectiveYToolDimRadius - casingDriftRadius;

  // YTool Bypass Tubing Dim circle
  // Smaller circle to represent bypass tubing inside the YTool
  const bypassTubingDimRadius =
    yToolDataContext.bypassTubingOuterDiameterIn / 2;
  const bypassTubingDimCenterX =
    minEffectiveYToolDimCenterX -
    minEffectiveYToolDimRadius +
    bypassTubingDimRadius;
  const bypassTubingDimCenterY = 0;
  const bypassTubingDimPoints = generateEquipmentDataPoints(
    bypassTubingDimRadius,
    bypassTubingDimCenterX,
    bypassTubingDimCenterY
  );
  yToolChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Y Block Bypass Tubing OD",
      yToolDataContext.bypassTubingOuterDiameterIn,
      yToolDataContext.lengthUnit
    ),
    xAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    yAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    points: bypassTubingDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, yToolDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, yToolDataContext.lengthUnit)
      };
    })
  });

  // Max Equipment Dim
  const maxEquipmentDimRadius =
    (yToolDataContext?.maxEquipmentOuterDiameterIn || 0) / 2;
  const maxEquipmentDimCenterX =
    bypassTubingDimCenterX + bypassTubingDimRadius + maxEquipmentDimRadius;
  const maxEquipmentDimCenterY = 0;
  const maxEquipmentDimPoints = generateEquipmentDataPoints(
    maxEquipmentDimRadius,
    maxEquipmentDimCenterX,
    maxEquipmentDimCenterY
  );
  yToolChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Max Equipment OD",
      yToolDataContext?.maxEquipmentOuterDiameterIn || 0,
      yToolDataContext.lengthUnit
    ),
    xAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    yAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    points: maxEquipmentDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, yToolDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, yToolDataContext.lengthUnit)
      };
    })
  });

  // Min Effective Y-Tool Dim
  const minEffectiveYToolDimCenterY = 0;
  const minEffectiveYToolDimPoints = generateEquipmentDataPoints(
    minEffectiveYToolDimRadius,
    minEffectiveYToolDimCenterX,
    minEffectiveYToolDimCenterY
  );
  yToolChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Max Equipment + Bypass Tubing OD",
      yToolDataContext.widthIn,
      yToolDataContext.lengthUnit
    ),
    xAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    yAxisLabel: `${yToolDataContext.lengthUnit.label}`,
    points: minEffectiveYToolDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, yToolDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, yToolDataContext.lengthUnit)
      };
    })
  });

  return yToolChartDataSet;
};
